import { Controller } from "@hotwired/stimulus"
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode"

export default class extends Controller {
  static targets = ["scanner", "result", "input", "startButton", "stopButton", "status", "overlay"]
  static values = {
    lookupUrl: String,
    redirectUrl: String
  }

  connect() {
    this.stream = null
    this.video = null
    this.barcodeDetector = null
    this.html5Scanner = null
    this.isScanning = false
    this.animationId = null
    this.useNativeApi = 'BarcodeDetector' in window
  }

  disconnect() {
    this.stopScanning()
  }

  async start() {
    if (this.isScanning) return

    if (this.useNativeApi) {
      await this.startNativeScanner()
    } else {
      await this.startHtml5Scanner()
    }
  }

  // Native BarcodeDetector API (Chrome, Safari)
  async startNativeScanner() {
    try {
      this.barcodeDetector = new BarcodeDetector({
        formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128']
      })

      // Simple camera constraints that work across devices
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      })

      this.video = document.createElement('video')
      this.video.srcObject = this.stream
      this.video.setAttribute('playsinline', '')
      this.video.setAttribute('autoplay', '')
      this.video.setAttribute('muted', '')
      this.video.style.width = '100%'
      this.video.style.height = 'auto'
      this.video.style.display = 'block'

      this.scannerTarget.innerHTML = ''
      this.scannerTarget.appendChild(this.video)

      // Wait for video to be ready
      await this.video.play()

      this.isScanning = true
      this.updateUIForScanning("native")

      // Start scanning
      this.scanFrame()
    } catch (err) {
      console.error("Native scanner failed:", err)
      // Try falling back to html5-qrcode
      console.log("Falling back to html5-qrcode")
      await this.startHtml5Scanner()
    }
  }

  async scanFrame() {
    if (!this.isScanning || !this.video) return

    try {
      const barcodes = await this.barcodeDetector.detect(this.video)
      if (barcodes.length > 0) {
        this.onScanSuccess(barcodes[0].rawValue)
        return
      }
    } catch (err) {
      // Ignore detection errors
    }

    this.animationId = requestAnimationFrame(() => this.scanFrame())
  }

  // Fallback: html5-qrcode library
  async startHtml5Scanner() {
    try {
      this.html5Scanner = new Html5Qrcode("barcode-scanner", {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.CODE_128
        ]
      })

      this.isScanning = true
      this.updateUIForScanning("html5")

      const config = {
        fps: 10,
        qrbox: (viewfinderWidth, viewfinderHeight) => {
          const minEdge = Math.min(viewfinderWidth, viewfinderHeight)
          return { width: Math.floor(minEdge * 0.8), height: Math.floor(minEdge * 0.4) }
        },
        aspectRatio: 1.777778,
        videoConstraints: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920, min: 640 },
          height: { ideal: 1080, min: 480 }
        }
      }

      await this.html5Scanner.start(
        { facingMode: "environment" },
        config,
        (decodedText) => this.onScanSuccess(decodedText),
        () => {} // Ignore errors (no barcode found)
      )
    } catch (err) {
      console.error("Html5 scanner failed:", err)
      this.showError("Failed to access camera. Please check permissions.")
    }
  }

  updateUIForScanning(mode = "") {
    this.startButtonTarget.classList.add("hidden")
    this.stopButtonTarget.classList.remove("hidden")
    this.overlayTarget.classList.add("active")
    const modeText = mode ? ` [${mode}]` : ""
    this.statusTarget.textContent = `Scanning...${modeText} Point camera at ISBN barcode`
    this.statusTarget.classList.remove("text-red-600", "text-green-600")
    this.statusTarget.classList.add("text-beige-600")
  }

  showError(message) {
    this.statusTarget.textContent = message
    this.statusTarget.classList.add("text-red-600")
    this.isScanning = false
    this.startButtonTarget.classList.remove("hidden")
    this.stopButtonTarget.classList.add("hidden")
    this.overlayTarget.classList.remove("active")
  }

  async stopScanning() {
    this.isScanning = false

    // Stop native scanner
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }
    if (this.video) {
      this.video.srcObject = null
      this.video = null
    }

    // Stop html5-qrcode scanner
    if (this.html5Scanner) {
      try {
        await this.html5Scanner.stop()
        this.html5Scanner.clear()
      } catch (err) {
        // Ignore
      }
      this.html5Scanner = null
    }

    this.scannerTarget.innerHTML = ''
  }

  async stop() {
    await this.stopScanning()
    this.startButtonTarget.classList.remove("hidden")
    this.stopButtonTarget.classList.add("hidden")
    this.overlayTarget.classList.remove("active")
    this.statusTarget.textContent = "Scanner stopped"
    this.statusTarget.classList.remove("text-green-600", "text-red-600")
  }

  async onScanSuccess(decodedText) {
    const isbn = decodedText.replace(/[^0-9X]/gi, "")

    if (isbn.length !== 10 && isbn.length !== 13) {
      this.statusTarget.textContent = `Invalid barcode: ${decodedText}`
      this.statusTarget.classList.add("text-red-600")
      return
    }

    await this.stopScanning()
    if (this.hasInputTarget) {
      this.inputTarget.value = isbn
    }
    this.statusTarget.textContent = `Found ISBN: ${isbn}`
    this.statusTarget.classList.remove("text-red-600")
    this.statusTarget.classList.add("text-green-600")

    this.startButtonTarget.classList.remove("hidden")
    this.stopButtonTarget.classList.add("hidden")
    this.overlayTarget.classList.remove("active")

    // Dispatch custom event for other controllers to listen to
    this.dispatch("scanned", { detail: { isbn } })

    // Only do lookup if we have a lookup URL configured
    if (this.hasLookupUrlValue && this.lookupUrlValue) {
      this.lookupIsbn(isbn)
    }
  }

  async lookupIsbn(isbn) {
    this.statusTarget.textContent = `Looking up ISBN: ${isbn}...`

    try {
      const response = await fetch(this.lookupUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ isbn: isbn })
      })

      const data = await response.json()

      if (response.ok) {
        this.showResult(data)
      } else {
        this.statusTarget.textContent = data.error || "Book not found"
        this.statusTarget.classList.add("text-red-600")
        this.resultTarget.innerHTML = this.renderNotFoundResult(isbn)
      }
    } catch (error) {
      console.error("Lookup error:", error)
      this.statusTarget.textContent = "Lookup failed"
      this.statusTarget.classList.add("text-red-600")
    }
  }

  showResult(data) {
    this.statusTarget.textContent = "Book found!"
    this.statusTarget.classList.remove("text-red-600")
    this.statusTarget.classList.add("text-green-600")

    this.resultTarget.innerHTML = this.renderResult(data)
  }

  renderResult(data) {
    return `
      <div class="card p-4 md:p-6 mt-4">
        <div class="flex gap-4 md:gap-6">
          <div class="shrink-0">
            ${data.cover_image_url
              ? `<img src="${data.cover_image_url}" alt="${data.title}" class="w-20 md:w-24 h-auto object-cover rounded-lg shadow">`
              : `<div class="w-20 md:w-24 h-28 md:h-36 bg-beige-200 rounded-lg flex items-center justify-center"><span class="text-beige-500 text-xs text-center px-2">No cover</span></div>`
            }
          </div>
          <div class="flex-1 min-w-0">
            <h3 class="text-lg md:text-xl font-semibold truncate">${data.title || "Unknown Title"}</h3>
            ${data.subtitle ? `<p class="text-beige-600 mt-1 text-sm truncate">${data.subtitle}</p>` : ""}
            ${data.authors ? `<p class="text-beige-700 font-fira mt-2 text-sm">by ${data.authors}</p>` : ""}
            <div class="flex flex-wrap gap-2 md:gap-4 mt-3 text-xs md:text-sm font-fira text-beige-600">
              ${data.publisher ? `<span>${data.publisher}</span>` : ""}
              ${data.publication_date ? `<span>${data.publication_date}</span>` : ""}
              ${data.page_count ? `<span>${data.page_count} pages</span>` : ""}
            </div>
          </div>
        </div>
        <div class="mt-4 md:mt-6 flex flex-col sm:flex-row gap-3">
          <a href="${this.redirectUrlValue}?isbn=${data.isbn_13 || data.isbn_10}" class="btn-primary text-center">
            Add to Library
          </a>
          <button type="button" data-action="barcode-scanner#start" class="btn-secondary text-center">
            Scan Another
          </button>
        </div>
      </div>
    `
  }

  renderNotFoundResult(isbn) {
    return `
      <div class="card p-4 md:p-6 mt-4 text-center">
        <svg class="w-12 h-12 mx-auto text-beige-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
        <h3 class="text-lg font-semibold mt-4">Book Not Found</h3>
        <p class="text-beige-600 font-fira mt-2 text-sm">We couldn't find a book with ISBN: ${isbn}</p>
        <div class="mt-4 md:mt-6 flex flex-col sm:flex-row justify-center gap-3">
          <a href="${this.redirectUrlValue}?isbn=${isbn}" class="btn-primary text-center">
            Add Manually
          </a>
          <button type="button" data-action="barcode-scanner#start" class="btn-secondary text-center">
            Try Again
          </button>
        </div>
      </div>
    `
  }

  manualLookup(event) {
    event.preventDefault()
    const isbn = this.inputTarget.value.replace(/[^0-9X]/gi, "")

    if (isbn.length !== 10 && isbn.length !== 13) {
      this.statusTarget.textContent = "Please enter a valid 10 or 13 digit ISBN"
      this.statusTarget.classList.add("text-red-600")
      return
    }

    this.lookupIsbn(isbn)
  }
}
